import { queryOptions, useQuery } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type {
  ApDocumentType,
  ApJobSearchResult,
  ApPricebookItemOption,
  ApProjectSearchResult,
  ApPurchaseOrderOption,
} from "@/accounts-payable/types";
import {
  JOB_SEARCH_LIMIT,
  MIN_JOB_SEARCH_LENGTH,
  MIN_PO_SEARCH_LENGTH,
  MIN_PRICEBOOK_SEARCH_LENGTH,
} from "@/accounts-payable/utils";
import { useApi } from "@/core/api";
import {
  AP_DOCUMENT_ENDPOINTS,
  jobSearchKey,
  pricebookSearchKey,
  projectSearchKey,
  purchaseOrderSearchKey,
} from "./keys";
import { AP_STALE_TIME } from "./polling";

export const apJobSearchQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  query: string,
  limit: number = JOB_SEARCH_LIMIT,
) => {
  const q = query.trim();
  return queryOptions({
    queryKey: jobSearchKey(businessId, q, limit),
    queryFn: ({ signal }) =>
      api
        .get<ApJobSearchResult[]>(`/businesses/${businessId}/ap/jobs/search`, {
          params: { q, limit },
          signal,
        })
        .then((response) => response.data),
    enabled: !!businessId && q.length >= MIN_JOB_SEARCH_LENGTH,
    staleTime: AP_STALE_TIME,
  });
};

export const useSearchJobs = (
  businessId?: string | null,
  query = "",
  limit: number = JOB_SEARCH_LIMIT,
) => {
  const api = useApi();
  return useQuery({
    ...apJobSearchQueryOptions(api, businessId, query, limit),
    placeholderData: (previousData) => previousData,
  });
};

/** Projects, for a business that files some purchases on the project itself. */
export const useSearchProjects = (businessId?: string | null, query = "") => {
  const api = useApi();
  const q = query.trim();
  return useQuery({
    queryKey: projectSearchKey(businessId, q),
    queryFn: ({ signal }) =>
      api
        .get<
          ApProjectSearchResult[]
        >(`/businesses/${businessId}/ap/projects/search`, { params: { q }, signal })
        .then((response) => response.data),
    enabled: !!businessId && q.length >= MIN_JOB_SEARCH_LENGTH,
    staleTime: AP_STALE_TIME,
    placeholderData: (previousData) => previousData,
  });
};

export const useSearchPricebookItems = (
  businessId?: string | null,
  query = "",
) => {
  const api = useApi();
  const q = query.trim();
  return useQuery({
    queryKey: pricebookSearchKey(businessId, q),
    queryFn: ({ signal }) =>
      api
        .get<
          ApPricebookItemOption[]
        >(`/businesses/${businessId}/ap/pricebook_items`, { params: { q }, signal })
        .then((response) => response.data),
    enabled: !!businessId && q.length >= MIN_PRICEBOOK_SEARCH_LENGTH,
    staleTime: AP_STALE_TIME,
    placeholderData: (previousData) => previousData,
  });
};

export const useSearchDocumentPurchaseOrders = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
  documentId: string,
  query = "",
) => {
  const api = useApi();
  const q = query.trim();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useQuery({
    queryKey: purchaseOrderSearchKey(businessId, documentId, q),
    queryFn: ({ signal }) =>
      api
        .get<
          ApPurchaseOrderOption[]
        >(`/businesses/${businessId}/ap/${endpoints.path}/${documentId}/purchase_orders`, { params: q ? { q } : {}, signal })
        .then((response) => response.data),
    enabled: !!businessId && (q === "" || q.length >= MIN_PO_SEARCH_LENGTH),
    staleTime: 0,
    placeholderData: (previousData) => previousData,
  });
};
